import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ModelAttributeOptionService} from '@shared/services/api/model-attribute-option.service';
import {CompanyService} from '@shared/services/api/company.service';
import {QuotationService} from '@shared/services/api/quotation.service';
import {ServiceService} from '@shared/services/api/service.service';
import {ServiceModel} from '@shared/models/service.model';
import {PopupService} from '@shared/services/popup.service';
import {SimpleReuseStrategy} from '@app/simple-reuse-strategy';
import {AppLoaderService} from '@app/shared/services/system/app-loader/app-loader.service';
import {NotifyService} from '@app/shared/services/notify.service';
import {AppConfirmService} from '@shared/services/system/app-confirm/app-confirm.service';
import {debounceTime, distinctUntilChanged, filter, switchMap} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';

@Component({
  selector: 'app-quotation-create',
  templateUrl: './quotation-create.component.html',
  styleUrls: ['./quotation-create.component.scss'],
  providers: [SimpleReuseStrategy]
})
export class QuotationCreateComponent implements OnInit {
  public quotationItemDisplayColumns = [
    {display: 'Type', key: 'item_type', useColor: false},
    {display: 'Quantity', key: 'item_quantity', useColor: false},
    {display: 'Name', key: 'item_name', useColor: false},
    {display: 'Price', key: 'item_unit_price', useColor: false},
    {display: 'Markup', key: 'item_unit_margin', useColor: false},
    {display: 'Total Price', key: 'item_total_price', useColor: false},
  ];
  @Input() serviceId;
  @Input() data: { serviceId: number; client_reference: string; materials: any[]};
  @Input() inPopup = false;

  @Output() onSubmitted: EventEmitter<any> = new EventEmitter();
  public itemFormGroup: FormGroup = new FormGroup({
    product_id: new FormControl(null),
    item_type: new FormControl('', Validators.required),
    item_name: new FormControl('', [
      Validators.required,
      Validators.maxLength(255),
    ]),
    item_description: new FormControl(''),
    item_unit_margin: new FormControl(0, Validators.compose([Validators.required, Validators.min(0)])),
    item_unit_price: new FormControl(0, Validators.compose([Validators.required, Validators.min(0)])),
    item_quantity: new FormControl(0, Validators.compose([Validators.required, Validators.min(0)])),
  });
  public formGroup: FormGroup = new FormGroup({
    type: new FormControl('', Validators.required),
    description: new FormControl(''),
    source_company_id: new FormControl(null, Validators.required),
    data: new FormGroup({
      service_id: new FormControl('', {updateOn: 'blur', validators: [Validators.required]}),
      out_of_hours_work_required: new FormControl('no'),
      we_require_another_discipline_contractor_for_these_works: new FormControl('no'),
      special_terms: new FormControl(''),
      client_reference: new FormControl(''),
      lead_in_time: new FormControl(''),
      work_duration: new FormControl(''),
      client_name: new FormControl({value: '', disabled: true}),
      building_name: new FormControl({value: '', disabled: true}),
      building_address: new FormControl({value: '', disabled: true}),
      discipline: new FormControl({value: '', disabled: true}),
    })
  });
  public quotationItems = [];
  public total = 0;
  public lineTotal = 0;
  public subtotal = 0;
  public vat = 0;
  public materials = [];

  public get suppliers() {
    return this.companyService.contractors;
  };

  public serviceIdIsValid = false;
  public service: ServiceModel;

  constructor(
    private fb: FormBuilder,
    private maoService: ModelAttributeOptionService,
    private http: HttpClient,
    private companyService: CompanyService,
    public quotationService: QuotationService,
    public serviceService: ServiceService,
    public popup: PopupService,
    private loader: AppLoaderService,
    private toast: NotifyService,
    private confirmService: AppConfirmService,
  ) {
  }

  ngOnInit() {
    this.buildItemForm();
    if (this.inPopup) {
      this.formGroup.get('data').patchValue({service_id: this.serviceId, client_reference: this.data.client_reference});
    }
    if (this.serviceId) {
      this.showService(this.serviceId);
    }
  }

  showService(serviceId) {
    let loader = this.loader.open();
    this.serviceService.show(serviceId)
      .finally(() => loader.close())
      .subscribe((data: ServiceModel) => {
        this.service = data;
        const materials = _.get(this.service, 'site_audit_materials', []);
        const quotationItems = [];
        const client_reference = _.get(this.service, 'data.client.client_reference', '');
        materials.forEach((item) => {
          quotationItems.push({
            item_type: 'material',
            item_name: item.name,
            item_description: item.description,
            item_unit_margin: this.quotationService.useMargin,
            item_unit_price: 0,
            item_quantity: item.quantity,
            use_margin: 0,
            item_total_price: 0
          });
        });
        this.quotationItems = [...quotationItems];
        this.formGroup.get('data').patchValue({
          discipline: data.discipline,
          client_name: data.client_name,
          building_name: data.building_name,
          client_reference: client_reference,
          building_address: data.building.address1 || data.building.address2,
        });
        this.computeTotal();
      });
  }

  resetItemFormGroup() {
    this.itemFormGroup.reset({
      product_id: null,
      item_type: '',
      item_name: '',
      item_description: '',
      item_unit_margin: this.quotationService.useMargin,
      item_unit_price: 0,
      item_quantity: 0,
      use_margin: 0,
    });
  }

  buildItemForm() {
    this.formGroup.get('data.service_id').valueChanges.subscribe((change) => {
      if (!change) {
        this.serviceIdIsValid = false;
      } else {
        this.serviceService.checkValid(change, 'quotation')
          .subscribe((data: any) => {
            if (data.valid) {
              this.serviceIdIsValid = true;
              this.showService(change);
            } else {
              this.serviceIdIsValid = false;
            }
          });
      }
    });

    this.itemFormGroup.valueChanges.subscribe((change) => {
      let item_quantity = change.item_quantity || 0;
      let item_unit_price = change.item_unit_price || 0;
      this.quotationService.useMargin = change.item_unit_margin || 0;
      this.lineTotal = item_quantity * item_unit_price * (1 + this.quotationService.useMargin / 100);
    });
    this.itemFormGroup.get('item_type').valueChanges.subscribe((change) => {
      this.quotationService.useMargin = change === 'service' ? 0 : this.quotationService.defaultMargin;
      this.itemFormGroup.patchValue({item_unit_margin: this.quotationService.useMargin, product_id: null}, {emitEvent: false});
      this.materials = [];
    });
    this.itemFormGroup.controls['item_name'].valueChanges
      .pipe(
        filter(text => {
          return (this.itemFormGroup.controls['item_type'].value === 'material' || this.itemFormGroup.controls['item_type'].value === 'service') && text.length > 2;
        }),
        distinctUntilChanged(),
        switchMap((res) => this.http.get('quotation-items/materials', {params: {keyword: res, type: this.itemFormGroup.controls['item_type'].value}}))
      )
      .subscribe((res: any) => {
        this.materials = res;
      });
  }

  materialName(item) {
    if (item) {
      if (item.code) {
        return item.code + '-' + item.name;
      } else {
        return item.name;
      }
    } else {
      return '';
    }
  }

  materialAutoComplete($event) {
    const item = $event.option.value;
    this.itemFormGroup.patchValue({
      product_id: item.id,
      item_unit_price: item.sell_price,
      item_description: item.description
    });
  }

  submit() {
    this.confirmService.confirm().subscribe((res) => {
      if (res) {
        let data = {items: this.quotationItems, ...this.formGroup.value, vat: this.vat};
        let loader = this.loader.open();
        this.quotationService.store(data)
          .finally(() => {
            loader.close();
          })
          .subscribe(() => {
            SimpleReuseStrategy.deleteRouteSnapshot('//quotation/create');
            this.onSubmitted.emit();
            this.toast.show('Quotation Added!');
          });
      }
    });
  }

  // Quotation Item Function

  addQuotationItem() {
    let item = this.itemFormGroup.value;
    if ((item.item_type === 'material' || item.item_type === 'service') && item.item_name.name) {
      item.item_name = item.item_name.name;
    }
    this.quotationItems.push({
      ...item,
      item_total_price: item.item_unit_price * item.item_quantity * (1 + item.item_unit_margin / 100)
    });
    this.quotationItems = [...this.quotationItems];
    this.computeTotal();
    this.resetItemFormGroup();
  }

  removeQuotationItem(row) {
    let index = this.quotationItems.indexOf(row);
    this.quotationItems.splice(index, 1);
    this.quotationItems = [...this.quotationItems];
    this.computeTotal();
  }

  computeTotal() {
    this.subtotal = _.sumBy(this.quotationItems, (item: any) => {
      return parseFloat(item.item_total_price);
    });
    this.total = this.subtotal + this.vat;
  }

  openEditServicePopup() {
    this.popup.openServiceEditPage(this.formGroup.value.data.serviceId);
  }

  openVatUpdatePopup() {
    this.popup
      .openVatCalculatePopup(this.subtotal, this.vat)
      .afterClosed()
      .subscribe(vatRes => {
        if (vatRes) {
          this.vat = vatRes.vat;
        }
      });

  }
}
